import { Close, Send, CheckCircleOutline } from "@mui/icons-material";
import {
  Button,
  CircularProgress,
  Paper,
  TableContainer,
  TextField,
  Box,
  Typography,
  Divider,
  Chip,
} from "@mui/material";
import { useState, useContext } from "react";
import { useOktaAuth } from "@okta/okta-react";
import { ModuleContext } from "../../context/ModuleContext";
import { UserModuleContext } from "../../context/UserModuleContext";

const WSU_RED = "#A60F2D";

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    "&:hover fieldset": { borderColor: WSU_RED },
    "&.Mui-focused fieldset": { borderColor: WSU_RED },
  },
  "& .MuiInputLabel-root.Mui-focused": { color: WSU_RED },
};

export const AddProxyAddress = () => {
  const [state, setState] = useState({
    error: null,
    waiting: false,
    newProxyAddress: "",
    comment: "",
    message: null,
  });

  const { setModule } = useContext(ModuleContext);
  const { userModule, setUserModule } = useContext(UserModuleContext);
  const { authState } = useOktaAuth();

  const networkId = userModule.userData["Network ID"];
  const existingAddresses = userModule.ADData["Proxy Address"] || [];

  const isFormValid = state.comment.trim() && state.newProxyAddress.trim();

  const addProxyAddress = async () => {
    setState((prev) => ({ ...prev, waiting: true, error: null }));

    let newProxyAddress = state.newProxyAddress.trim();
    if (!newProxyAddress.toLowerCase().startsWith("smtp:")) {
      newProxyAddress = "smtp:" + newProxyAddress;
    }

    const proxyAddresses = [...existingAddresses, newProxyAddress];

    try {
      const res = await fetch(
          `${process.env.REACT_APP_AWS_API_GATEWAY_ENDPOINT}/user/ad/attributes`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
              Authorization: authState.accessToken.accessToken,
            },
            body: JSON.stringify({
              networkId,
              comment: state.comment,
              dn: userModule.ADData["DN"],
              attributes: { proxyAddresses },
              callerId: authState.accessToken.claims.sub,
            }),
          }
      );

      if (!res.ok) {
        const text = await res.text();
        const errorObj = JSON.parse(text);
        throw new Error(errorObj?.error?.message || "Unknown server error");
      }

      await res.json();

      setUserModule((prev) => ({
        ...prev,
        ADData: { ...prev.ADData, "Proxy Address": proxyAddresses },
      }));

      setState((prev) => ({
        ...prev,
        comment: "",
        newProxyAddress: "",
        message: "Proxy address has been added successfully.",
        waiting: false,
        error: null,
      }));
    } catch (error) {
      setState((prev) => ({ ...prev, waiting: false, error }));
    }
  };

  if (state.waiting) {
    return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
          <CircularProgress style={{ color: WSU_RED }} size={80} thickness={5} />
        </Box>
    );
  }

  if (state.message) {
    return (
        <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>
          <Box sx={{ backgroundColor: "#2e7d32", color: "white", px: 3, py: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
            <CheckCircleOutline fontSize="small" />
            <Typography variant="subtitle2" fontWeight={700}>
              Proxy Address Added
            </Typography>
          </Box>

          <Box sx={{ px: 3, pt: 3, pb: 1 }}>
            <Typography variant="overline" sx={{ color: WSU_RED, fontWeight: 700, letterSpacing: 1.5 }}>
              Confirmation
            </Typography>
          </Box>

          <Box sx={{ px: 3, py: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {state.message}
            </Typography>
          </Box>

          <Box sx={{ px: 3, pb: 4, display: "flex", justifyContent: "flex-end" }}>
            <Button
                variant="outlined"
                endIcon={<Close />}
                onClick={() => setModule((prev) => ({ ...prev, isVisible: false }))}
                sx={{ borderColor: "#ccc", color: "#666", "&:hover": { borderColor: "#aaa", backgroundColor: "#f9f9f9" } }}
            >
              Close
            </Button>
          </Box>
        </TableContainer>
    );
  }

  return (
      <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>

        {/* Error Banner */}
        {state.error && (
            <Box sx={{ backgroundColor: WSU_RED, color: "white", px: 3, py: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                Failed to add proxy address
              </Typography>
              <Typography variant="body2">{state.error.message}</Typography>
            </Box>
        )}

        {/* ── Section: Current Proxy Addresses ── */}
        <Box sx={{ px: 3, pt: 3, pb: 1 }}>
          <Typography variant="overline" sx={{ color: WSU_RED, fontWeight: 700, letterSpacing: 1.5 }}>
            Current Proxy Addresses
          </Typography>
          <Typography variant="caption" sx={{ display: "block", color: "#777", mt: 0.25 }}>
            {networkId}
          </Typography>
        </Box>

        {existingAddresses.length > 0 ? (
            <Box sx={{ px: 3, py: 2, display: "flex", flexWrap: "wrap", gap: 1 }}>
              {existingAddresses.map((addr) => (
                  <Chip
                      key={addr}
                      label={addr}
                      size="small"
                      sx={{
                        backgroundColor: "#fdf0f2",
                        border: `1px solid ${WSU_RED}`,
                        color: WSU_RED,
                        fontWeight: 500,
                        fontSize: "0.75rem",
                      }}
                  />
              ))}
            </Box>
        ) : (
            <Box sx={{ px: 3, py: 1.5 }}>
              <Typography variant="body2" color="text.disabled" fontStyle="italic">
                No existing proxy addresses
              </Typography>
            </Box>
        )}

        <Divider sx={{ mx: 3 }} />

        {/* ── Section: New Address ── */}
        <Box sx={{ px: 3, pt: 3, pb: 1 }}>
          <Typography variant="overline" sx={{ color: WSU_RED, fontWeight: 700, letterSpacing: 1.5 }}>
            New Address
          </Typography>
          <Typography variant="caption" sx={{ display: "block", color: "#777", mt: 0.25 }}>
            The smtp: prefix will be added automatically if omitted
          </Typography>
        </Box>

        <Box sx={{ px: 3, pb: 3 }}>
          <TextField
              required
              fullWidth
              size="small"
              label="New Proxy Address"
              value={state.newProxyAddress}
              onChange={(e) => setState((prev) => ({ ...prev, newProxyAddress: e.target.value }))}
              placeholder="e.g. alias@wsu.edu"
              sx={fieldSx}
          />
        </Box>

        <Divider sx={{ mx: 3 }} />

        {/* ── Section: Justification ── */}
        <Box sx={{ px: 3, pt: 3, pb: 1 }}>
          <Typography variant="overline" sx={{ color: WSU_RED, fontWeight: 700, letterSpacing: 1.5 }}>
            Justification
          </Typography>
        </Box>

        <Box sx={{ px: 3, pb: 3 }}>
          <TextField
              required
              fullWidth
              size="small"
              multiline
              rows={3}
              label="Comment"
              value={state.comment}
              onChange={(e) => setState((prev) => ({ ...prev, comment: e.target.value }))}
              placeholder="Provide a reason for adding this proxy address"
              sx={fieldSx}
          />
        </Box>

        {/* ── Actions ── */}
        <Box sx={{ px: 3, pb: 4, display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <Button
              variant="outlined"
              endIcon={<Close />}
              onClick={() => setModule((prev) => ({ ...prev, isVisible: false }))}
              sx={{
                borderColor: "#ccc",
                color: "#666",
                "&:hover": { borderColor: "#aaa", backgroundColor: "#f9f9f9" },
              }}
          >
            Cancel
          </Button>
          <Button
              variant="contained"
              endIcon={<Send />}
              onClick={addProxyAddress}
              disabled={!isFormValid}
              sx={{
                backgroundColor: WSU_RED,
                color: "white",
                px: 4,
                py: 1.25,
                fontWeight: 600,
                letterSpacing: 0.5,
                "&:hover": { backgroundColor: "#8a0c25" },
                "&.Mui-disabled": { backgroundColor: "#ddd", color: "#aaa" },
              }}
          >
            Add Proxy Address
          </Button>
        </Box>

      </TableContainer>
  );
};